import clsx from "clsx";

import { Checkbox, PercentInput, Select } from "@components/v2";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";
import { parseDateRangeArgument } from "@utils/index";

import { useUniversalBenchmarksConfiguratorContext } from "../hooks";

export const AdjustForPromotionsAndOutliers = () => {
  const { baselineFilters, setBaselineFilters } =
    useUniversalBenchmarksConfiguratorContext();

  return (
    <div className="py-6 pl-6 pr-3">
      <h3 className="text-base font-semibold">
        Adjust for promotions & outliers
      </h3>
      <p className="text-sm text-v2-secondary mb-1">
        Remove seasonal, high promotion, and unusual days that will skew your
        results.
      </p>
      <div className="py-4 border-b border-v2-tertiary">
        <div className="mb-2">
          <Checkbox
            label="Remove promotion outliers"
            checked={baselineFilters.discountsThreshold.enabled}
            onChange={(event) => {
              setBaselineFilters({
                ...baselineFilters,
                discountsThreshold: {
                  ...baselineFilters.discountsThreshold,
                  enabled: event.target.checked,
                },
              });
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div>Exclude days where discounts exceed</div>
          <PercentInput
            disabled={!baselineFilters.discountsThreshold.enabled}
            className="w-[50px]"
            defaultValue={20}
            value={baselineFilters.discountsThreshold.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  discountsThreshold: {
                    ...baselineFilters.discountsThreshold,
                    value,
                  },
                });
              }
            }}
          />
          <div>of</div>
          <Select
            buttonClassName="w-[200px]"
            disabled={!baselineFilters.discountsThreshold.enabled}
            options={[
              { value: "blended", label: "first time and returning" },
              { value: "first-time", label: "first time" },
            ]}
            value={baselineFilters.discountsThreshold.method}
            onChange={(method) => {
              if (method) {
                setBaselineFilters({
                  ...baselineFilters,
                  discountsThreshold: {
                    ...baselineFilters.discountsThreshold,
                    method,
                  },
                });
              }
            }}
          />
          <div>revenue</div>
        </div>
      </div>
      <div className="py-4 border-b border-v2-tertiary">
        <div className="mb-2">
          <Checkbox
            label="Remove revenue outliers"
            checked={baselineFilters.highestRevenueThreshod.enabled}
            onChange={(event) => {
              setBaselineFilters({
                ...baselineFilters,
                highestRevenueThreshod: {
                  ...baselineFilters.highestRevenueThreshod,
                  enabled: event.target.checked,
                },
                lowestRevenueThreshod: {
                  ...baselineFilters.lowestRevenueThreshod,
                  enabled: event.target.checked,
                },
              });
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          Exclude the top
          <PercentInput
            disabled={!baselineFilters.highestRevenueThreshod.enabled}
            className="w-[50px]"
            defaultValue={5}
            value={baselineFilters.highestRevenueThreshod.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  highestRevenueThreshod: {
                    ...baselineFilters.highestRevenueThreshod,
                    value,
                  },
                });
              }
            }}
          />
          and bottom
          <PercentInput
            disabled={!baselineFilters.lowestRevenueThreshod.enabled}
            className="w-[50px]"
            defaultValue={5}
            value={baselineFilters.lowestRevenueThreshod.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  lowestRevenueThreshod: {
                    ...baselineFilters.lowestRevenueThreshod,
                    value,
                  },
                });
              }
            }}
          />
          of revenue.
        </div>
      </div>
      <div className="py-4 border-b border-v2-tertiary">
        <div className="mb-2">
          <Checkbox
            label="Remove order outliers"
            checked={baselineFilters.highestOrdersThreshod.enabled}
            onChange={(event) => {
              setBaselineFilters({
                ...baselineFilters,
                highestOrdersThreshod: {
                  ...baselineFilters.highestOrdersThreshod,
                  enabled: event.target.checked,
                },
                lowestOrdersThreshod: {
                  ...baselineFilters.lowestOrdersThreshod,
                  enabled: event.target.checked,
                },
              });
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          Exclude the top
          <PercentInput
            disabled={!baselineFilters.highestOrdersThreshod.enabled}
            className="w-[50px]"
            defaultValue={5}
            value={baselineFilters.highestOrdersThreshod.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  highestOrdersThreshod: {
                    ...baselineFilters.highestOrdersThreshod,
                    value,
                  },
                });
              }
            }}
          />
          and bottom
          <PercentInput
            disabled={!baselineFilters.lowestOrdersThreshod.enabled}
            className="w-[50px]"
            defaultValue={5}
            value={baselineFilters.lowestOrdersThreshod.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  lowestOrdersThreshod: {
                    ...baselineFilters.lowestOrdersThreshod,
                    value,
                  },
                });
              }
            }}
          />
          of order numbers.
        </div>
      </div>
      <div className="py-4 border-b border-v2-tertiary">
        <div className="mb-2">
          <Checkbox
            label="Remove spend outliers"
            checked={baselineFilters.highestSpendThreshod.enabled}
            onChange={(event) => {
              setBaselineFilters({
                ...baselineFilters,
                highestSpendThreshod: {
                  ...baselineFilters.highestSpendThreshod,
                  enabled: event.target.checked,
                },
                lowestSpendThreshod: {
                  ...baselineFilters.lowestSpendThreshod,
                  enabled: event.target.checked,
                },
              });
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          Exclude the top
          <PercentInput
            disabled={!baselineFilters.highestSpendThreshod.enabled}
            className="w-[50px]"
            defaultValue={5}
            value={baselineFilters.highestSpendThreshod.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  highestSpendThreshod: {
                    ...baselineFilters.highestSpendThreshod,
                    value,
                  },
                });
              }
            }}
          />
          and bottom
          <PercentInput
            disabled={!baselineFilters.lowestSpendThreshod.enabled}
            className="w-[50px]"
            defaultValue={5}
            value={baselineFilters.lowestSpendThreshod.value}
            onChange={(value) => {
              if (value) {
                setBaselineFilters({
                  ...baselineFilters,
                  lowestSpendThreshod: {
                    ...baselineFilters.lowestSpendThreshod,
                    value,
                  },
                });
              }
            }}
          />
          of media spend.
        </div>
      </div>
      <div className="py-4">
        <div>Exclude a date range</div>
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {baselineFilters.excludedDateRanges.map((dateRange) => {
            const interval = parseDateRangeArgument(
              dateRange,
              todayLocalAsISODate(),
            );

            return (
              <div
                key={JSON.stringify(dateRange)}
                className={clsx(
                  "flex items-center justify-between text-xs rounded-full px-4 py-2 bg-cosmic-3",
                )}
              >
                {formatDateRange(interval)}
                <button
                  className="ml-4"
                  onClick={() => {
                    setBaselineFilters({
                      ...baselineFilters,
                      excludedDateRanges:
                        baselineFilters.excludedDateRanges.filter(
                          (e) => e !== dateRange,
                        ),
                    });
                  }}
                >
                  <i className="fa-solid fa-xmark hover:text-mars"></i>
                </button>
              </div>
            );
          })}
        </div>
        <DateRangeCalendarSelect
          value={null}
          onApply={(v) => {
            setBaselineFilters({
              ...baselineFilters,
              excludedDateRanges: [...baselineFilters.excludedDateRanges, v],
            });
          }}
          buttonComponent={(ref, refProps) => (
            <button
              ref={ref}
              {...refProps}
              className="button sm tertiary-button font-bold"
            >
              ADD EXCLUSION
            </button>
          )}
        />
      </div>
    </div>
  );
};
